/**
 * Global non-blocking notifications (snackbar + persisted inbox) per
 * docs/NOTIFICATION_CENTER_AND_DIALOGS_SPEC.md (v1.1+). Blocking dialogs live in
 * AppDialogContext (useAppDialog) — do not fold dialog types into notify.*.
 *
 * @see docs/NOTIFICATION_CENTER_AND_DIALOGS_SPEC.md
 * @see docs/NOTIFICATION_SYSTEM_REFACTOR.md
 * @see docs/RICH_NOTIFICATION_AND_DIALOG_TRUST_CONTRACT.md (notify.rich trust boundaries)
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { Alert, Box, Slide, Snackbar, Typography, useMediaQuery, useTheme } from "@mui/material";
import { AlertTriangle, CheckCircle2, Info, XCircle } from "lucide-react";

import { toDisplayErrorMessage } from "@/utils/errorMessage";
import { IS_DEV } from "@/utils/env";
import {
  addInboxItem,
  countUnreadInbox,
  createInboxItem,
  dismissInboxItem,
  markAllInboxRead,
  markInboxItemRead,
  sortInboxNewestFirst,
} from "./inboxReducer";
import { DEFAULT_NOTIFICATION_DELIVERY_BY_SEVERITY, NOTIFICATION_DELIVERY } from "./notificationConstants";
import {
  buildNotificationStorageKey,
  loadInboxItems,
  saveInboxItemsSync,
  subscribeInboxCrossTab,
} from "./notificationPersistence";

// SECURITY: Centralized notification system prevents inconsistent user feedback
// RISK: Rapid successive calls could cause notification overlap - handled via key prop and
// same-message deduplication while a notification is already displayed.
// ASSUMPTION: Single snackbar at a time is acceptable UX for this application
// ASSUMPTION: Duplicate message while one is shown is ignored to avoid flicker and spam.
// ASSUMPTION: Message is sanitized (coerced to string, control chars stripped) and rendered
// as plain text; do not pass unsanitized user- or API-originated HTML or script content.

const NotificationContext = createContext(null);

/** Max time (ms) snackbar stays open before auto-dismiss; applies to all severities and caps `notify.rich` overrides. */
const SNACKBAR_AUTO_HIDE_MS = 3000;

export const SEVERITY_META = {
  success: {
    title: "Saved",
    railColor: "var(--success, #0D9F6E)",
    iconColor: "var(--success, #0D9F6E)",
    iconBackground: "#F1FAF6",
    surface: "#F8FCFB",
    borderColor: "#D2EDE3",
    shadow:
      "0 3px 10px rgba(15, 23, 42, 0.2), 0 10px 28px rgba(15, 23, 42, 0.12), 0 20px 48px rgba(13, 159, 110, 0.16)",
    autoHideDuration: SNACKBAR_AUTO_HIDE_MS,
    Icon: CheckCircle2,
  },
  error: {
    title: "Could not complete",
    railColor: "var(--destructive, #AC2330)",
    iconColor: "var(--destructive, #AC2330)",
    iconBackground: "#FEF5F5",
    surface: "#FEFBFB",
    borderColor: "#F0D4D6",
    shadow:
      "0 3px 10px rgba(15, 23, 42, 0.22), 0 12px 32px rgba(15, 23, 42, 0.12), 0 22px 52px rgba(172, 35, 48, 0.22)",
    autoHideDuration: SNACKBAR_AUTO_HIDE_MS,
    Icon: XCircle,
  },
  warning: {
    title: "Heads up",
    railColor: "var(--warning, #F1C107)",
    iconColor: "var(--warning-foreground, #101010)",
    iconBackground: "#FEF8E6",
    surface: "#FFFDF7",
    borderColor: "#EBDC9A",
    shadow:
      "0 3px 10px rgba(15, 23, 42, 0.2), 0 12px 32px rgba(15, 23, 42, 0.11), 0 22px 52px rgba(241, 193, 7, 0.2)",
    autoHideDuration: SNACKBAR_AUTO_HIDE_MS,
    Icon: AlertTriangle,
  },
  info: {
    title: "Update",
    railColor: "var(--primary, #9D6AC4)",
    iconColor: "var(--primary, #9D6AC4)",
    iconBackground: "#F5F2FA",
    surface: "#FAF8FD",
    borderColor: "#E4DAF0",
    shadow:
      "0 3px 10px rgba(15, 23, 42, 0.2), 0 10px 28px rgba(15, 23, 42, 0.12), 0 20px 48px rgba(157, 106, 196, 0.16)",
    autoHideDuration: SNACKBAR_AUTO_HIDE_MS,
    Icon: Info,
  },
};

const VALID_SEVERITIES = ["success", "error", "warning", "info"];

const MAX_SAFE_MESSAGE_LENGTH = 500;

/**
 * Strip control chars (keep tab/LF/CR) and cap length for snackbar/inbox text.
 * @param {string} s
 * @returns {string}
 */
function sanitizePlainText(s) {
  const cleaned = Array.from(s)
    .filter((char) => {
      const code = char.charCodeAt(0);
      return code === 9 || code === 10 || code === 13 || code >= 32;
    })
    .join("");
  if (cleaned.length <= MAX_SAFE_MESSAGE_LENGTH) return cleaned;
  return `${cleaned.slice(0, MAX_SAFE_MESSAGE_LENGTH - 1)}…`;
}

/**
 * Coerce and sanitize message for display / persistence (plain text).
 * Avoids classic `String({})` → "[object Object]" toast bodies.
 * @param {unknown} value
 * @returns {string}
 */
export const toSafeMessage = (value) => {
  if (value == null) return "";
  if (typeof value === "string" && !value.trim()) return "";
  return sanitizePlainText(toDisplayErrorMessage(value, "Unexpected error"));
};

/**
 * @param {'success'|'error'|'warning'|'info'} severity
 * @param {string} delivery
 * @returns {{ snackbar: boolean, inbox: boolean, inboxRead: boolean }}
 */
function deliveryToChannels(delivery, severity) {
  const d =
    delivery === NOTIFICATION_DELIVERY.SNACKBAR_ONLY ||
    delivery === NOTIFICATION_DELIVERY.SNACKBAR_AND_INBOX ||
    delivery === NOTIFICATION_DELIVERY.INBOX_ONLY
      ? delivery
      : (DEFAULT_NOTIFICATION_DELIVERY_BY_SEVERITY[severity] ?? NOTIFICATION_DELIVERY.SNACKBAR_ONLY);

  return {
    snackbar: d === NOTIFICATION_DELIVERY.SNACKBAR_ONLY || d === NOTIFICATION_DELIVERY.SNACKBAR_AND_INBOX,
    inbox: d === NOTIFICATION_DELIVERY.INBOX_ONLY || d === NOTIFICATION_DELIVERY.SNACKBAR_AND_INBOX,
    inboxRead: d === NOTIFICATION_DELIVERY.SNACKBAR_AND_INBOX,
  };
}

const MOBILE_BREAKPOINT = "sm";

/**
 * @param {Object} opts
 * @param {() => void} opts.appendInbox
 * @param {(msg: string, sev: string) => void} opts.showSnackbar
 * @param {'success'|'error'|'warning'|'info'} opts.severity
 * @param {string} opts.safeMessage
 * @param {Record<string, unknown>} [opts.options]
 */
function dispatchNonBlocking({ appendInbox, showSnackbar, severity, safeMessage, options = {} }) {
  const delivery = /** @type {{ delivery?: string }} */ (options).delivery;
  const { snackbar, inbox, inboxRead } = deliveryToChannels(delivery, severity);
  const actionIds = Array.isArray(options.actionIds) ? options.actionIds.filter((x) => typeof x === "string") : undefined;
  const actionRouteRaw = options.actionRoute;
  const actionLabelRaw = options.actionLabel;
  const actionRoute =
    typeof actionRouteRaw === "string" && toSafeMessage(actionRouteRaw).trim().length > 0
      ? toSafeMessage(actionRouteRaw).trim()
      : undefined;
  const actionLabel =
    typeof actionLabelRaw === "string" && toSafeMessage(actionLabelRaw).trim().length > 0
      ? toSafeMessage(actionLabelRaw).trim()
      : undefined;

  if (inbox && safeMessage) {
    appendInbox({
      severity,
      message: safeMessage,
      read: inboxRead,
      actionIds,
      actionRoute,
      actionLabel,
    });
  }

  if (snackbar && safeMessage) {
    const titleRaw = options.title;
    const title =
      typeof titleRaw === "string" && toSafeMessage(titleRaw).trim().length > 0
        ? toSafeMessage(titleRaw).trim()
        : undefined;
    showSnackbar(safeMessage, severity, title);
  }
}

export const NotificationProvider = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(MOBILE_BREAKPOINT));

  const [state, setState] = useState({
    open: false,
    message: "",
    content: null,
    severity: "info",
    autoHideDuration: SNACKBAR_AUTO_HIDE_MS,
    onDismiss: null,
    /** When false, MUI Alert omits `onClose` so the snackbar is only dismissed via explicit actions (e.g. notify.rich buttons). */
    alertClosable: true,
    title: null,
    key: 0,
  });

  const [inboxItems, setInboxItems] = useState([]);
  const storageKeyRef = useRef("");

  const anchorOrigin = isMobile ? { vertical: "bottom", horizontal: "center" } : { vertical: "top", horizontal: "center" };

  const SnackbarSlide = useMemo(
    () =>
      React.forwardRef(function SnackbarSlide(props, ref) {
        return <Slide {...props} ref={ref} direction={isMobile ? "up" : "down"} />;
      }),
    [isMobile]
  );

  const showSnackbarOnly = useCallback((message, severity = "success", title) => {
    const safeMessage = toSafeMessage(message);
    const safeSeverity = VALID_SEVERITIES.includes(severity) ? severity : "info";
    const safeTitle =
      typeof title === "string" && toSafeMessage(title).trim().length > 0 ? toSafeMessage(title).trim() : null;
    setState((prev) => {
      if (prev.open && prev.message === safeMessage && prev.title === safeTitle) {
        return prev;
      }
      return {
        open: true,
        message: safeMessage,
        content: null,
        severity: safeSeverity,
        autoHideDuration: SEVERITY_META[safeSeverity].autoHideDuration,
        onDismiss: null,
        alertClosable: true,
        title: safeTitle,
        key: Date.now(),
      };
    });
  }, []);

  const appendInbox = useCallback(({ severity, message, read, actionIds, actionRoute, actionLabel }) => {
    const safeSeverity = VALID_SEVERITIES.includes(severity) ? severity : "info";
    const id =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `n-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const routeRaw = typeof actionRoute === "string" ? toSafeMessage(actionRoute).trim() : "";
    const labelRaw = typeof actionLabel === "string" ? toSafeMessage(actionLabel).trim() : "";
    const item = createInboxItem({
      id,
      createdAt: Date.now(),
      severity: safeSeverity,
      message: toSafeMessage(message),
      read,
      actionIds,
      actionRoute: routeRaw || undefined,
      actionLabel: labelRaw || undefined,
    });
    setInboxItems((prev) => addInboxItem(prev, item));
  }, []);

  /**
   * @param {unknown} message
   * @param {string} [severity]
   * @param {Record<string, unknown>} [options] notify options (delivery, actionIds)
   */
  const notifyWithDelivery = useCallback(
    (message, severity = "success", options = {}) => {
      const safeSeverity = VALID_SEVERITIES.includes(severity) ? severity : "info";
      const safeMessage = toSafeMessage(message);
      dispatchNonBlocking({
        appendInbox,
        showSnackbar: showSnackbarOnly,
        severity: safeSeverity,
        safeMessage,
        options,
      });
    },
    [appendInbox, showSnackbarOnly]
  );

  const showRich = useCallback(
    (content, severity = "info", options = {}) => {
      const safeSeverity = VALID_SEVERITIES.includes(severity) ? severity : "info";
      const delivery = options?.delivery ?? DEFAULT_NOTIFICATION_DELIVERY_BY_SEVERITY[safeSeverity];
      const { snackbar, inbox, inboxRead } = deliveryToChannels(delivery, safeSeverity);
      const inboxMessage = toSafeMessage(options?.inboxMessage ?? "");
      const actionIds = Array.isArray(options.actionIds)
        ? options.actionIds.filter((x) => typeof x === "string")
        : undefined;
      const inboxReadFinal = options?.inboxUnread === true ? false : inboxRead;
      const actionRouteOpt = options?.actionRoute;
      const actionLabelOpt = options?.actionLabel;

      if (inbox) {
        if (!inboxMessage) {
          if (IS_DEV) {
            console.warn("[notify.rich] Inbox delivery requires options.inboxMessage (plain text). Skipping inbox row.");
          }
        } else {
          appendInbox({
            severity: safeSeverity,
            message: inboxMessage,
            read: inboxReadFinal,
            actionIds,
            actionRoute: typeof actionRouteOpt === "string" ? actionRouteOpt : undefined,
            actionLabel: typeof actionLabelOpt === "string" ? actionLabelOpt : undefined,
          });
        }
      }

      if (snackbar) {
        let nextAutoHide = SEVERITY_META[safeSeverity].autoHideDuration;
        if (options?.autoHideDuration === null) {
          nextAutoHide = null;
        } else if (Number.isFinite(options?.autoHideDuration)) {
          nextAutoHide = Math.min(SNACKBAR_AUTO_HIDE_MS, Math.max(0, options.autoHideDuration));
        }
        const alertClosable = options?.closable !== false;
        setState({
          open: true,
          message: "",
          content: content ?? null,
          severity: safeSeverity,
          autoHideDuration: nextAutoHide,
          onDismiss: typeof options?.onDismiss === "function" ? options.onDismiss : null,
          alertClosable,
          title: null,
          key: Date.now(),
        });
      }
    },
    [appendInbox]
  );

  const hide = useCallback(() => {
    setState((prev) => {
      if (typeof prev.onDismiss === "function") {
        try {
          prev.onDismiss();
        } catch (dismissError) {
          console.error("Notification dismiss callback failed:", dismissError);
        }
      }
      return { ...prev, open: false };
    });
  }, []);

  const dismissInboxNotification = useCallback((id) => {
    setInboxItems((prev) => dismissInboxItem(prev, id));
  }, []);

  const markInboxNotificationRead = useCallback((id) => {
    setInboxItems((prev) => markInboxItemRead(prev, id));
  }, []);

  const markAllInboxNotificationsRead = useCallback(() => {
    setInboxItems((prev) => markAllInboxRead(prev));
  }, []);

  const clearAllInboxNotifications = useCallback(() => {
    setInboxItems([]);
  }, []);

  const restoreInboxNotifications = useCallback((items) => {
    setInboxItems(Array.isArray(items) ? items : []);
  }, []);

  const setNotificationInboxScope = useCallback(({ userId, customer }) => {
    const key = userId ? buildNotificationStorageKey(userId, customer || "") : "";
    storageKeyRef.current = key;
    if (!key) {
      setInboxItems([]);
      return;
    }
    setInboxItems(loadInboxItems(key));
  }, []);

  useEffect(() => {
    const key = storageKeyRef.current;
    if (!key) return undefined;
    const t = window.setTimeout(() => {
      saveInboxItemsSync(key, inboxItems);
    }, 150);
    return () => window.clearTimeout(t);
  }, [inboxItems]);

  useEffect(() => {
    return subscribeInboxCrossTab((changedKey) => {
      if (changedKey && changedKey === storageKeyRef.current) {
        setInboxItems(loadInboxItems(changedKey));
      }
    });
  }, []);

  const notify = useMemo(
    () => ({
      success: (msg, options) =>
        notifyWithDelivery(msg, "success", options != null && typeof options === "object" ? options : {}),
      error: (msg, options) =>
        notifyWithDelivery(msg, "error", options != null && typeof options === "object" ? options : {}),
      warning: (msg, options) =>
        notifyWithDelivery(msg, "warning", options != null && typeof options === "object" ? options : {}),
      info: (msg, options) => notifyWithDelivery(msg, "info", options != null && typeof options === "object" ? options : {}),
      rich: showRich,
      /** Snackbar-only primitive; does not write the inbox. */
      show: showSnackbarOnly,
    }),
    [notifyWithDelivery, showRich, showSnackbarOnly]
  );

  const handleClose = (event, reason) => {
    if (reason === "clickaway") return;
    hide();
  };

  const renderSeverityIcon = useCallback((severity) => {
    const severityMeta = SEVERITY_META[severity] ?? SEVERITY_META.info;
    const SeverityIcon = severityMeta.Icon;

    return (
      <Box
        sx={{
          width: 31,
          height: 31,
          borderRadius: "999px",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: severityMeta.iconBackground,
          color: severityMeta.iconColor,
          flexShrink: 0,
        }}
      >
        <SeverityIcon size={22} strokeWidth={2} aria-hidden />
      </Box>
    );
  }, []);

  const renderTextContent = useCallback((severity, message, title) => {
    const severityMeta = SEVERITY_META[severity] ?? SEVERITY_META.info;
    const displayTitle = title ?? severityMeta.title;

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
        <Typography
          sx={{
            fontSize: "0.92rem",
            letterSpacing: "0.01em",
            fontWeight: 600,
            color: "var(--gray-500, #555555)",
            lineHeight: 1.25,
          }}
        >
          {displayTitle}
        </Typography>
        <Typography
          sx={{
            fontSize: "1.125rem",
            fontWeight: 500,
            color: "var(--foreground, #2D3748)",
            lineHeight: 1.38,
          }}
        >
          {message}
        </Typography>
      </Box>
    );
  }, []);

  const unreadInboxCount = useMemo(() => countUnreadInbox(inboxItems), [inboxItems]);
  const inboxItemsNewestFirst = useMemo(() => sortInboxNewestFirst(inboxItems), [inboxItems]);

  const snackbarElement = (
    <Snackbar
      key={state.key}
      open={state.open}
      autoHideDuration={state.autoHideDuration}
      onClose={handleClose}
      anchorOrigin={anchorOrigin}
      slots={{ transition: SnackbarSlide }}
      sx={{
        ...(isMobile
          ? {
              bottom: "max(18px, env(safe-area-inset-bottom)) !important",
              left: "0 !important",
              right: "0 !important",
              width: "100% !important",
              maxWidth: "100% !important",
              boxSizing: "border-box",
              paddingLeft: "max(16px, env(safe-area-inset-left))",
              paddingRight: "max(16px, env(safe-area-inset-right))",
            }
          : {
              top: "max(26px, env(safe-area-inset-top)) !important",
              bottom: "auto !important",
            }),
      }}
    >
      <Alert
        onClose={state.alertClosable ? handleClose : undefined}
        severity={state.severity}
        icon={renderSeverityIcon(state.severity)}
        variant="standard"
        sx={{
          width: isMobile ? "100%" : "min(462px, calc(100vw - 53px))",
          maxWidth: "100%",
          borderRadius: "13px",
          border: `1px solid ${SEVERITY_META[state.severity].borderColor}`,
          borderLeft: `4px solid ${SEVERITY_META[state.severity].railColor}`,
          boxShadow: SEVERITY_META[state.severity].shadow,
          background: SEVERITY_META[state.severity].surface,
          color: "var(--foreground, #2D3748)",
          alignItems: "flex-start",
          minHeight: isMobile ? "62px" : "57px",
          py: isMobile ? 1.1 : 1.15,
          pl: isMobile ? 1.25 : 1.35,
          pr: isMobile ? 0.65 : 0.9,
          "& .MuiAlert-icon": {
            marginTop: "1px",
            marginRight: "11px",
            padding: 0,
          },
          "& .MuiAlert-message": {
            width: "100%",
            padding: 0,
          },
          "& .MuiAlert-action": {
            marginRight: 0,
            paddingLeft: "6px",
            paddingTop: "2px",
          },
          "& .MuiAlert-action .MuiIconButton-root": {
            color: "var(--gray-500, #555555)",
          },
        }}
      >
        {state.content || renderTextContent(state.severity, state.message, state.title)}
      </Alert>
    </Snackbar>
  );

  const contextValue = useMemo(
    () => ({
      notify,
      show: showSnackbarOnly,
      hide,
      inboxItems: inboxItemsNewestFirst,
      unreadInboxCount,
      dismissInboxNotification,
      markInboxNotificationRead,
      markAllInboxNotificationsRead,
      clearAllInboxNotifications,
      restoreInboxNotifications,
      setNotificationInboxScope,
    }),
    [
      notify,
      showSnackbarOnly,
      hide,
      inboxItemsNewestFirst,
      unreadInboxCount,
      dismissInboxNotification,
      markInboxNotificationRead,
      markAllInboxNotificationsRead,
      clearAllInboxNotifications,
      restoreInboxNotifications,
      setNotificationInboxScope,
    ]
  );

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
      {ReactDOM.createPortal(snackbarElement, document.body)}
    </NotificationContext.Provider>
  );
};

/**
 * @returns {{
 *   notify: {
 *     success: (msg: string, options?: { delivery?: string, actionIds?: string[] }) => void,
 *     error: (msg: string, options?: { delivery?: string, actionIds?: string[] }) => void,
 *     warning: (msg: string, options?: { delivery?: string, actionIds?: string[] }) => void,
 *     info: (msg: string, options?: { delivery?: string, actionIds?: string[] }) => void,
 *     rich: (content: React.ReactNode, severity?: string, options?: Record<string, unknown>) => void,
 *     show: (msg: string, severity?: string) => void,
 *   },
 *   show: (msg: string, severity?: string) => void,
 *   hide: () => void,
 *   inboxItems: import('./inboxReducer').InboxItem[],
 *   unreadInboxCount: number,
 *   dismissInboxNotification: (id: string) => void,
 *   markInboxNotificationRead: (id: string) => void,
 *   markAllInboxNotificationsRead: () => void,
 *   clearAllInboxNotifications: () => void,
 *   restoreInboxNotifications: (items: import('./inboxReducer').InboxItem[]) => void,
 *   setNotificationInboxScope: (scope: { userId: string, customer: string }) => void,
 * }}
 */
export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error("useNotification must be used within NotificationProvider");
  }
  return context;
};

export { NOTIFICATION_DELIVERY, DEFAULT_NOTIFICATION_DELIVERY_BY_SEVERITY };

export default NotificationContext;
